import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { useActiveOrg } from "common-fe";
import { Box, Typography, CircularProgress } from "@mui/material";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { manifest } from "score-common/permissions";

// AC#4 (#641) — the app requires login (ProtectedRoute, App.jsx) AND rejects a
// user without score_announcer (or an org-scoped score role) in the active
// org. "Org-scoped" = a role that carries list_shows_org (the whole show), not
// list_shows_mine (score_judge/score_viewer, scoped to shows that user is
// personally assigned to — not enough for an announcer, who needs the whole
// ring). Derived from the manifest rather than hardcoded so a future role that
// adds list_shows_org is picked up automatically.
//
// Mirrors scoreadmin-fe's ScoreEntitlementGate role check: the access token
// carries a `roles` claim, a per-org map `{ orgId: [roleNames] }` stamped by
// account's PreTokenGeneration trigger. Match qualified ("score:score_admin")
// or bare ("score_admin"). The API independently enforces this (score-api
// permission checks); this is the FE affordance.
const ORG_SCOPED_ROLES = Object.entries(manifest.roles)
  .filter(([, role]) => role.permissions.includes("list_shows_org"))
  .map(([key]) => key);

function activeOrgHasAnnouncerAccess(accessToken, activeOrgId) {
  if (!accessToken || !activeOrgId) return false;
  try {
    const claims = jwtDecode(accessToken);
    const raw = claims.roles;
    const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
    const roles = Array.isArray(map[activeOrgId]) ? map[activeOrgId] : [];
    return roles.some(
      (r) => ORG_SCOPED_ROLES.includes(r) || ORG_SCOPED_ROLES.includes(r?.replace(/^score:/, "")),
    );
  } catch {
    return false;
  }
}

export default function AnnouncerRoleGate({ children }) {
  const auth = useAuth();
  const { activeOrgId, isSuper, loading } = useActiveOrg();
  const { t } = useTranslation("announcer");
  const accessToken = auth.user?.access_token;

  // Wait for the org list/selection to resolve before deciding — otherwise a
  // legitimately-entitled announcer would flash the gated message on first
  // paint.
  if (loading) {
    // #714 — primary.main is dark grey and invisible on a dark background;
    // force the brand accent so this gate's loading state stays visible.
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: colors.foxOrange }} />
      </Box>
    );
  }

  if (isSuper || activeOrgHasAnnouncerAccess(accessToken, activeOrgId)) {
    return children;
  }

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", py: 8, px: 3, textAlign: "center" }}>
      <Typography variant="h5" gutterBottom color="text.primary">
        {t("gate.heading")}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t("gate.body")}
      </Typography>
    </Box>
  );
}
